import { useEffect, useMemo, useState } from 'react';
import { fmtDateTime } from '@/lib/format';
import type { ZetaSnapshot } from '@/types/domain';

interface Props {
  /** Снимки zeta_snapshots по одному символу (в любом порядке). */
  rows: ZetaSnapshot[];
  /** Биржи, доступные для выбора (уже без исключённых). */
  exchanges: string[];
  exchangeLabel: (exchange: string) => string;
}

const BID_COLOR = '#2ECC71';
const ASK_COLOR = '#E74C3C';
const VIEW_W = 1000;
const VIEW_H = 300;
const PAD_Y = 12;

function fmtAxisPrice(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return Number(value.toPrecision(6)).toString();
}

/**
 * Строит path для ряда точек; нулевые/пустые цены (нет котировки) разрывают линию.
 * Координаты — в системе viewBox (VIEW_W × VIEW_H).
 */
function buildPath(
  points: Array<{ t: number; v: number | null }>,
  x: (t: number) => number,
  y: (v: number) => number,
): string {
  let d = '';
  let penDown = false;
  for (const p of points) {
    if (p.v == null) {
      penDown = false;
      continue;
    }
    d += `${penDown ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`;
    penDown = true;
  }
  return d;
}

/** Компактный inline-SVG график Bid/Ask по времени для выбранной биржи. */
export function ZetaHistoryChart({ rows, exchanges, exchangeLabel }: Props): JSX.Element | null {
  const [exchange, setExchange] = useState(exchanges[0] ?? '');

  useEffect(() => {
    if (!exchanges.length) return;
    if (!exchanges.includes(exchange)) setExchange(exchanges[0]);
  }, [exchanges, exchange]);

  const chart = useMemo(() => {
    const points = rows
      .filter((r) => r.exchange === exchange)
      .map((r) => ({
        t: new Date(r.taken_at).getTime(),
        bid: Number(r.bid) > 0 ? Number(r.bid) : null,
        ask: Number(r.ask) > 0 ? Number(r.ask) : null,
      }))
      .filter((p) => Number.isFinite(p.t))
      .sort((a, b) => a.t - b.t);
    const values = points.flatMap((p) => [p.bid, p.ask]).filter((v): v is number => v != null);
    if (!points.length || !values.length) return null;
    const tMin = points[0].t;
    const tMax = points[points.length - 1].t;
    let vMin = Math.min(...values);
    let vMax = Math.max(...values);
    if (vMin === vMax) {
      const pad = vMin === 0 ? 1 : Math.abs(vMin) * 0.05;
      vMin -= pad;
      vMax += pad;
    }
    const x = (t: number): number => (tMax === tMin ? VIEW_W / 2 : ((t - tMin) / (tMax - tMin)) * VIEW_W);
    const y = (v: number): number => PAD_Y + (1 - (v - vMin) / (vMax - vMin)) * (VIEW_H - PAD_Y * 2);
    const bidPath = buildPath(points.map((p) => ({ t: p.t, v: p.bid })), x, y);
    const askPath = buildPath(points.map((p) => ({ t: p.t, v: p.ask })), x, y);
    const last = points[points.length - 1];
    return { tMin, tMax, vMin, vMax, bidPath, askPath, count: points.length, last, single: points.length === 1, x, y };
  }, [rows, exchange]);

  if (!exchanges.length) return null;

  return (
    <div style={{ padding: '0 var(--space-lg) var(--space-md)', display: 'grid', gap: 'var(--space-xs)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--tg-hint)', fontSize: 12 }}>График:</span>
        <select
          value={exchange}
          onChange={(e) => setExchange(e.target.value)}
          style={{
            background: 'var(--tg-secondary-bg)',
            color: 'var(--tg-text)',
            border: '0.5px solid var(--tg-separator)',
            borderRadius: 'var(--radius-sm)',
            padding: '2px var(--space-sm)',
            fontSize: 12,
            minHeight: 28,
          }}
        >
          {exchanges.map((ex) => (
            <option key={ex} value={ex}>{exchangeLabel(ex)}</option>
          ))}
        </select>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--tg-text)' }}>
          <span style={{ width: 12, height: 2, background: BID_COLOR, display: 'inline-block' }} />
          Bid
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--tg-text)' }}>
          <span style={{ width: 12, height: 2, background: ASK_COLOR, display: 'inline-block' }} />
          Ask
        </span>
      </div>
      {!chart ? (
        <div style={{ color: 'var(--tg-hint)', fontSize: 12 }}>Нет котировок для графика.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'stretch' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                color: 'var(--tg-hint)',
                fontSize: 10,
                textAlign: 'right',
                minWidth: 40,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <span>{fmtAxisPrice(chart.vMax)}</span>
              <span>{fmtAxisPrice(chart.vMin)}</span>
            </div>
            <svg
              viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
              preserveAspectRatio="none"
              role="img"
              aria-label={`График Bid/Ask: ${exchangeLabel(exchange)}`}
              style={{
                flex: 1,
                minWidth: 0,
                height: 140,
                display: 'block',
                borderLeft: '0.5px solid var(--tg-separator)',
                borderBottom: '0.5px solid var(--tg-separator)',
              }}
            >
              <line
                x1={0}
                x2={VIEW_W}
                y1={VIEW_H / 2}
                y2={VIEW_H / 2}
                stroke="var(--tg-hint)"
                strokeOpacity={0.25}
                strokeDasharray="4 4"
                vectorEffect="non-scaling-stroke"
              />
              <path d={chart.bidPath} fill="none" stroke={BID_COLOR} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
              <path d={chart.askPath} fill="none" stroke={ASK_COLOR} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
              {chart.single && chart.last.bid != null && (
                <circle cx={chart.x(chart.last.t)} cy={chart.y(chart.last.bid)} r={4} fill={BID_COLOR} />
              )}
              {chart.single && chart.last.ask != null && (
                <circle cx={chart.x(chart.last.t)} cy={chart.y(chart.last.ask)} r={4} fill={ASK_COLOR} />
              )}
            </svg>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              color: 'var(--tg-hint)',
              fontSize: 10,
              paddingLeft: 44,
              gap: 'var(--space-sm)',
            }}
          >
            <span>{fmtDateTime(new Date(chart.tMin).toISOString())}</span>
            <span>{`${chart.count} точек`}</span>
            <span>{fmtDateTime(new Date(chart.tMax).toISOString())}</span>
          </div>
        </>
      )}
    </div>
  );
}
